// 行业维度 = 薄共享维度。权威源 = 后端 xb_industry(IND_* 前缀,平台运营维护),经
// GET /platform/industries 拉取 —— 不再前端硬编码(库里增删行业前端自动跟随,治本前端拷贝漂移)。
// 后端返回扁平真实行业(都是 level-1);本服务合成一个不可选的 IND_ALL 根 + 把真实行业挂成 level-2 叶,
// 以满足消费方既有的 2 级树/过滤逻辑(消费方零改)。租户存的是 code,与 xb_industry.code 一致。
import { api } from "@/shared/api";

export interface IndustryRow {
  id: number;
  code: string;
  parentCode: string | null;
  level: number;
  name: string;
  nameI18n: Record<string, string> | null;
  description: string | null;
  attributes: string[] | null;
  geoPolicy: string | null;
  icon: string | null;
  csPromptHint: string | null;
  posterPromptHint: string | null;
  coachingPromptHint: string | null;
  defaultRoleKey: string | null;
  defaultToneKey: string | null;
  sortWeight: number;
  status: string;
  isSystem: boolean;
}

// local-only: A (frontend query helper)
export interface ListIndustryQuery {
  level?: number;
  status?: string;
  keyword?: string;
}

// local-only: B — policyConfig.phaseDefaults map value(类型仍被 persona/generated 消费,保留)
export interface PhaseDefault {
  roleKey: string | null;
  toneKey: string | null;
}

// local-only: B — policyConfig.compliance
export interface IndustryCompliance {
  forbiddenPhrases: string[];
  requiredDisclaimers: string[];
}

// local-only: B — 行业级业务配置类型(被 persona/generated 引用,保留类型)
export interface PolicyConfig {
  industryCode: string;
  phaseDefaults: Record<string, PhaseDefault>;
  compliance: IndustryCompliance;
  posterVisualHint: string | null;
  posterCta: string | null;
}

const NULL_HINTS = {
  nameI18n: null,
  description: null,
  attributes: null,
  geoPolicy: null,
  icon: null,
  csPromptHint: null,
  posterPromptHint: null,
  coachingPromptHint: null,
  defaultRoleKey: null,
  defaultToneKey: null,
  isSystem: true,
} as const;

const SYNTHETIC_ROOT = "IND_ALL";

/** 后端 GET /platform/industries 的投影(只读字段)。 */
interface IndustryViewDto {
  id: number;
  code: string;
  parentCode: string | null;
  level: number;
  name: string;
  sortWeight: number;
  status: string;
}

// 后端扁平真实行业 → 消费方期望的 2 级树:合成不可选的 IND_ALL 根 + 真行业作 level-2 叶。
function toTree(dtos: IndustryViewDto[]): IndustryRow[] {
  const leaves: IndustryRow[] = dtos
    .filter((d) => d.code !== SYNTHETIC_ROOT)
    .map((d) => ({ ...d, parentCode: SYNTHETIC_ROOT, level: 2, ...NULL_HINTS }));
  const root: IndustryRow = {
    id: 900,
    code: SYNTHETIC_ROOT,
    parentCode: null,
    level: 1,
    name: "行业",
    sortWeight: 0,
    status: "ACTIVE",
    ...NULL_HINTS,
  };
  return [root, ...leaves];
}

async function fetchIndustries(path: string, q: ListIndustryQuery): Promise<IndustryRow[]> {
  const dtos = (await api.get<IndustryViewDto[]>(path)) ?? [];
  let rows = toTree(dtos);
  if (q.status) rows = rows.filter((r) => r.status === q.status);
  if (q.level != null) rows = rows.filter((r) => r.level === q.level);
  if (q.keyword) {
    const kw = q.keyword;
    rows = rows.filter((r) => r.name.includes(kw) || r.code.includes(kw));
  }
  return rows;
}

export const industryService = {
  /**
   * 列出行业(后端 xb_industry 真源;status/keyword/level 客户端过滤)。平台控制台用 /platform/industries。
   * ★0926:后端不带 status 只回启用行业 —— 调用方没限定 ACTIVE 时带 status=ALL,平台才看得到停用行业
   *   (如租户详情里挂在停用行业上的租户,行业名不再显示成裸 code)。
   */
  list: (q: ListIndustryQuery = {}): Promise<IndustryRow[]> =>
    fetchIndustries(
      q.status === "ACTIVE" ? "/platform/industries" : "/platform/industries?status=ALL",
      q
    ),
  /** 租户可达版(/tenant/industries);内容同 list,只换租户 namespace —— 避免租户撞 /platform/** 的 403。 */
  listForTenant: (q: ListIndustryQuery = {}): Promise<IndustryRow[]> =>
    fetchIndustries("/tenant/industries", q),
};
